import React from 'react';
import {
    StyledPaginate,
    StyledContentPaginate,
    StyledButtonAction,
    StyledButtonPage,
    StyledEllipsis,
    StyledMobileIndicator,
    StyledPageNumbers
} from 'styled/UI/StyledPaginate';

const ChevronLeft = () => (
    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M15 18L9 12L15 6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const ChevronRight = () => (
    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M9 18L15 12L9 6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

function generatePages(currentPage, lastPage, firstPage) {
    const pages = [];
    const siblings = 1;

    pages.push(firstPage);

    const leftSibling = Math.max(currentPage - siblings, firstPage + 1);
    const rightSibling = Math.min(currentPage + siblings, lastPage - 1);

    if (leftSibling > firstPage + 1) {
        pages.push('left-ellipsis');
    } else if (firstPage + 1 < lastPage) {
        pages.push(firstPage + 1);
    }

    for (let i = leftSibling; i <= rightSibling; i++) {
        if (!pages.includes(i)) {
            pages.push(i);
        }
    }

    if (rightSibling < lastPage - 1) {
        pages.push('right-ellipsis');
    } else if (lastPage - 1 > firstPage && !pages.includes(lastPage - 1)) {
        pages.push(lastPage - 1);
    }

    if (lastPage > firstPage && !pages.includes(lastPage)) {
        pages.push(lastPage);
    }

    return pages;
}

export default function Paginate({
    marginTop,
    marginRight,
    marginBottom,
    marginLeft,
    currentPage,
    totalItems,
    lastPage,
    perPage,
    firstPage = 1,
    onPaginate = () => false,
    onNextClick = () => false,
    onPreviousClick,
    // backward compat for typo in legacy prop
    onPreviusClick
}) {
    const handlePrevious = onPreviousClick || onPreviusClick || (() => {});

    const isFirstPage = currentPage === firstPage;
    const isLastPage = currentPage === lastPage;

    const previousPage = () => {
        if (!isFirstPage) {
            onPaginate(currentPage - 1);
            handlePrevious();
        }
    };

    const nextPage = () => {
        if (!isLastPage) {
            onPaginate(currentPage + 1);
            onNextClick();
        }
    };

    const pages = generatePages(currentPage, lastPage, firstPage);

    if (lastPage <= 1) return null;

    return (
        <StyledPaginate
            aria-label="Paginação"
            marginTop={marginTop}
            marginRight={marginRight}
            marginBottom={marginBottom}
            marginLeft={marginLeft}
        >
            <StyledContentPaginate>
                <StyledButtonAction
                    onClick={previousPage}
                    disabled={isFirstPage}
                    aria-label="Página anterior"
                >
                    <ChevronLeft />
                </StyledButtonAction>

                <StyledPageNumbers>
                    {pages.map((page) => {
                        if (page === 'left-ellipsis' || page === 'right-ellipsis') {
                            return (
                                <StyledEllipsis key={page} aria-hidden="true">
                                    &hellip;
                                </StyledEllipsis>
                            );
                        }

                        return (
                            <StyledButtonPage
                                key={page}
                                onClick={() => onPaginate(page)}
                                active={page === currentPage}
                                aria-label={`Página ${page}`}
                                aria-current={page === currentPage ? 'page' : undefined}
                            >
                                {page}
                            </StyledButtonPage>
                        );
                    })}
                </StyledPageNumbers>

                <StyledMobileIndicator aria-live="polite">
                    <strong>{currentPage}</strong> / {lastPage}
                </StyledMobileIndicator>

                <StyledButtonAction
                    onClick={nextPage}
                    disabled={isLastPage}
                    aria-label="Próxima página"
                >
                    <ChevronRight />
                </StyledButtonAction>
            </StyledContentPaginate>
        </StyledPaginate>
    );
}
